import type {
  AdminUsageDashboardResponse,
  AdminUsageDepartmentBreakdown,
  AdminUsageFilterOption,
  AdminUsageModelBreakdown,
  AdminUsageSeatRow,
  AdminUsageTrendPoint,
} from '@nadoutong/contracts';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAdminUsageDashboardCsv, type AdminUsageDashboardClientQuery } from '../../api/usage.ts';
import { useDisplayRate } from '../../api/useBilling.ts';
import { useAdminUsageDashboard } from '../../api/useUsage.ts';
import { StackedBars, Meter, Sparkline, stackedBarsSwatchClass } from '../../charts/index.ts';
import type { StackedBarsSeries } from '../../charts/index.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { useToast } from '../../components/Toast.tsx';
import {
  formatTokens,
  formatUsdCentsAsCny,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
} from '../../components/formatters.ts';
import { useSession } from '../../hooks/useSession.tsx';
import { useLocale, useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  DateRangePicker,
  Input,
  KpiCard,
  PageHeader,
  Pagination,
  Panel,
  Select,
  Skeleton,
  Switch,
  TBody,
  Table,
  TableSkeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  Td,
  Th,
  THead,
  Tr,
  cx,
} from '../../ui/index.ts';
import type { DateRangePreset, SelectOption } from '../../ui/index.ts';
import {
  dayRangeToIsoWindow,
  formatCompactCount,
  formatIsoDate,
  formatLatencyMs,
  formatNullablePercent,
  formatPercent,
  formatShortTime,
  monthToDateRange,
  resolveRangeSelection,
  rollingDayRange,
  testIdSlug,
  todayLocalDay,
} from './format.ts';

/**
 * Admin usage analytics (`org:usage:read`) — epic #897 PR 6.
 *
 * One filter bar owns the query; the date window renders exactly once
 * (in the filter panel's caption row — the old page repeated it three
 * times). Primitive filters mirror into the URL search params
 * (`replace`, no history spam) so `/usage?apiKeyId=…` cross-links from
 * the Requests page land pre-filtered. Seat search + pagination stay
 * ephemeral component state.
 *
 * The whole view is fed by ONE dashboard query, so loading/error are
 * page-level: a skeleton grid while pending, a single inline retry on
 * failure (no toast for load failures — DESIGN.md single-channel
 * errors). The CSV export is the page's one action: its outcome
 * surfaces as a success toast or an inline `role=alert` next to the
 * button (the old page swallowed success and buried the error at the
 * bottom of the page).
 */

type TokenMetric = 'total' | 'in' | 'out';
type Granularity = 'day' | 'week' | 'month';
type GroupBy = 'model' | 'department' | 'member';

type TranslationFn = ReturnType<typeof useT>;

const ALL = 'all';
const SEAT_PAGE_SIZE = 12;

const DEFAULT_RANGE = '30d';
// Matches the platform-api admin-dashboard hard cap (90-day window).
const MAX_RANGE_SPAN_DAYS = 90;

function adminRangePresets(today: string, t: TranslationFn): readonly DateRangePreset[] {
  return [
    { id: '7d', label: t('usage.range.7d'), range: rollingDayRange(today, 7) },
    { id: '30d', label: t('usage.range.30d'), range: rollingDayRange(today, 30) },
    { id: '90d', label: t('usage.range.90d'), range: rollingDayRange(today, 90) },
    { id: 'month', label: t('usage.range.month'), range: monthToDateRange(today) },
  ];
}

function parseGranularity(value: string | null): Granularity {
  return value === 'week' || value === 'month' ? value : 'day';
}

function parseGroupBy(value: string | null): GroupBy {
  return value === 'department' || value === 'member' ? value : 'model';
}

function isRevokedOption(option: AdminUsageFilterOption): boolean {
  return typeof option.revokedAt === 'string' && option.revokedAt.length > 0;
}

export function AdminUsage(): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const displayRateQ = useDisplayRate();
  const [searchParams, setSearchParams] = useSearchParams();

  const today = todayLocalDay();
  const rangePresets = useMemo(() => adminRangePresets(today, t), [today, t]);
  const rangeSelection = useMemo(
    () =>
      resolveRangeSelection({
        rangeParam: searchParams.get('range'),
        fromParam: searchParams.get('from'),
        toParam: searchParams.get('to'),
        presets: rangePresets,
        defaultPresetId: DEFAULT_RANGE,
        maxSpanDays: MAX_RANGE_SPAN_DAYS,
        today,
      }),
    [searchParams, rangePresets, today],
  );
  const granularity = parseGranularity(searchParams.get('granularity'));
  const groupBy = parseGroupBy(searchParams.get('groupBy'));
  const model = searchParams.get('model') ?? '';
  // #1672: the model dimension is keyed by vendor + model, so the selection
  // needs both. The dropdown's option id is the composite of the two.
  const vendor = searchParams.get('vendor') ?? '';
  const apiKeyId = searchParams.get('apiKeyId') ?? '';
  const department = searchParams.get('department') ?? '';
  const memberAccountId = searchParams.get('member') ?? '';

  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [metric, setMetric] = useState<TokenMetric>('total');
  const [showRevoked, setShowRevoked] = useState(apiKeyId.length > 0);
  const [exportState, setExportState] = useState<'idle' | 'working' | 'error'>('idle');

  function patchParams(patch: Readonly<Record<string, string | null>>): void {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value.length === 0) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
    setOffset(0);
  }

  const queryRange = useMemo(() => dayRangeToIsoWindow(rangeSelection.range), [rangeSelection.range]);
  const query = useMemo<AdminUsageDashboardClientQuery>(
    () => ({
      ...queryRange,
      granularity,
      groupBy,
      model: model.length > 0 ? model : undefined,
      vendor: model.length > 0 && vendor.length > 0 ? vendor : undefined,
      apiKeyId: apiKeyId.length > 0 ? apiKeyId : undefined,
      department: department.length > 0 ? department : undefined,
      memberAccountId: memberAccountId.length > 0 ? memberAccountId : undefined,
      q: search.length > 0 ? search : undefined,
      limit: SEAT_PAGE_SIZE,
      offset,
    }),
    [apiKeyId, department, granularity, groupBy, memberAccountId, model, offset, queryRange, search, vendor],
  );
  const selectedModelOptionId = model.length === 0 || vendor.length === 0 ? model : `${vendor}:${model}`;
  const dashboardQ = useAdminUsageDashboard(query, true);
  const dashboard = dashboardQ.data;
  const modelOptions = useMemo(() => {
    const options = dashboard?.filters.models ?? [];
    if (model.length === 0 || vendor.length > 0) return options;
    if (options.some((option) => option.id === selectedModelOptionId)) return options;
    // A link made before #1672 carries a bare model name, which still selects
    // the union across vendors. Give that state an option of its own, so the
    // control cannot read "all models" while the data is filtered to one name.
    return [
      { id: model, label: t('usage.admin.filter.model_all_vendors', { model }), vendor: '', model },
      ...options,
    ];
  }, [dashboard?.filters.models, model, selectedModelOptionId, t, vendor]);
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  async function handleExport(): Promise<void> {
    setExportState('working');
    try {
      const csv = await getAdminUsageDashboardCsv(fetchImpl, query);
      const rangeSlug =
        rangeSelection.presetId ?? `${rangeSelection.range.from}_${rangeSelection.range.to}`;
      triggerCsvDownload(csv, `usage-dashboard-${rangeSlug}.csv`);
      setExportState('idle');
      toast.success(t('usage.admin.export.done'));
    } catch {
      setExportState('error');
    }
  }

  return (
    <div data-testid="usage-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('usage.admin.title')}
        sub={t('usage.admin.subtitle')}
        testId="usage-admin-header"
        actions={
          <div className="flex items-center gap-3">
            {exportState === 'error' ? (
              <p className="m-0 text-dense text-bad" role="alert" data-testid="usage-export-error">
                {t('usage.admin.export.error')}
              </p>
            ) : null}
            <Button
              variant="secondary"
              loading={exportState === 'working'}
              onClick={() => {
                void handleExport();
              }}
              data-testid="usage-export-csv"
            >
              {t('usage.admin.export.idle')}
            </Button>
          </div>
        }
      />

      <Panel
        caption={t('usage.admin.filter.caption')}
        padded
        testId="usage-admin-filter-bar"
        actions={
          dashboard !== undefined ? (
            <span className="font-mono text-micro text-paper-low" data-testid="usage-window-meta">
              {t('usage.admin.window.meta', {
                from: formatIsoDate(dashboard.window.from),
                to: formatIsoDate(dashboard.window.to),
                time: formatShortTime(dashboard.refreshedAt),
              })}
            </span>
          ) : undefined
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker
            value={rangeSelection.range}
            onChange={(range, presetId) => {
              if (presetId !== null) patchParams({ range: presetId, from: null, to: null });
              else patchParams({ range: 'custom', from: range.from, to: range.to });
            }}
            presets={rangePresets}
            activePresetId={rangeSelection.presetId}
            maxSpanDays={MAX_RANGE_SPAN_DAYS}
            locale={locale}
            ariaLabel={t('usage.range.aria_label')}
            labels={{
              prevMonth: t('usage.range.prev_month'),
              nextMonth: t('usage.range.next_month'),
            }}
            testId="usage-range-picker"
          />
          <Tabs value={granularity} onValueChange={(next) => patchParams({ granularity: next })}>
            <TabsList ariaLabel={t('usage.admin.granularity.aria')}>
              <TabsTrigger value="day" testId="usage-granularity-day">
                {t('usage.admin.granularity.day')}
              </TabsTrigger>
              <TabsTrigger value="week" testId="usage-granularity-week">
                {t('usage.admin.granularity.week')}
              </TabsTrigger>
              <TabsTrigger value="month" testId="usage-granularity-month">
                {t('usage.admin.granularity.month')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Select
            value={groupBy}
            onValueChange={(next) => patchParams({ groupBy: parseGroupBy(next) })}
            options={[
              { value: 'model', label: t('usage.admin.group_by.model') },
              { value: 'department', label: t('usage.admin.group_by.department') },
              { value: 'member', label: t('usage.admin.group_by.member') },
            ]}
            ariaLabel={t('usage.admin.group_by_value.model')}
            className="w-44"
            testId="usage-filter-group-by"
          />
          <FilterSelect
            value={selectedModelOptionId}
            onChange={(next) => {
              const option = modelOptions.find((entry) => entry.id === next);
              patchParams({ model: option?.model ?? null, vendor: option?.vendor ?? null });
            }}
            allLabel={t('usage.admin.filter.all_models')}
            ariaLabel={t('usage.admin.filter.model')}
            options={modelOptions}
            testId="usage-filter-model"
          />
          <FilterSelect
            value={apiKeyId}
            onChange={(next) => patchParams({ apiKeyId: next })}
            allLabel={t('usage.admin.filter.all_keys')}
            ariaLabel={t('usage.admin.filter.api_key')}
            options={(dashboard?.filters.apiKeys ?? [])
              .filter((option) => showRevoked || !isRevokedOption(option) || option.id === apiKeyId)
              .map((option) => ({
                ...option,
                label: isRevokedOption(option)
                  ? `${option.label}${t('usage.admin.filter.revoked_suffix')}`
                  : option.label,
              }))}
            testId="usage-filter-api-key"
          />
          <Switch
            checked={showRevoked}
            onCheckedChange={(next) => {
              setShowRevoked(next);
              const selected = (dashboard?.filters.apiKeys ?? []).find(
                (option) => option.id === apiKeyId,
              );
              if (!next && selected !== undefined && isRevokedOption(selected)) {
                patchParams({ apiKeyId: null });
              }
            }}
            label={t('usage.admin.filter.show_revoked_keys')}
            testId="usage-filter-show-revoked"
          />
          <FilterSelect
            value={department}
            onChange={(next) => patchParams({ department: next })}
            allLabel={t('usage.admin.filter.all_departments')}
            ariaLabel={t('usage.admin.filter.department')}
            options={dashboard?.filters.departments ?? []}
            testId="usage-filter-department"
          />
          <FilterSelect
            value={memberAccountId}
            onChange={(next) => patchParams({ member: next })}
            allLabel={t('usage.admin.filter.all_members')}
            ariaLabel={t('usage.admin.filter.member')}
            options={dashboard?.filters.members ?? []}
            testId="usage-filter-member"
          />
        </div>
      </Panel>

      {dashboardQ.isLoading ? (
        <AdminSkeleton />
      ) : dashboardQ.isError ? (
        <Panel caption={t('usage.admin.title')} padded testId="usage-admin-error">
          <PanelError
            message={t('usage.error.load')}
            retryLabel={t('usage.retry')}
            onRetry={() => {
              void dashboardQ.refetch();
            }}
          />
        </Panel>
      ) : dashboard !== undefined ? (
        <>
          <SummaryGrid dashboard={dashboard} cnyPerUsd={cnyPerUsd} t={t} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
            <TrendPanel
              points={dashboard.trend}
              groupBy={dashboard.window.groupBy}
              metric={metric}
              onMetric={setMetric}
              totalTokens={dashboard.summary.totalInputTokens + dashboard.summary.totalOutputTokens}
            />
            <ModelMixPanel models={dashboard.models} />
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <DepartmentMixPanel departments={dashboard.departments} />
            <ServiceHealthPanel dashboard={dashboard} />
          </div>
          <SeatUsageTable
            dashboard={dashboard}
            cnyPerUsd={cnyPerUsd}
            search={search}
            onSearch={(next) => {
              setSearch(next);
              setOffset(0);
            }}
            onOffset={setOffset}
          />
        </>
      ) : null}
    </div>
  );
}

function FilterSelect(props: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly allLabel: string;
  readonly ariaLabel: string;
  readonly options: readonly { readonly id: string; readonly label: string }[];
  readonly testId: string;
}): JSX.Element {
  const options: SelectOption[] = [
    { value: ALL, label: props.allLabel },
    ...props.options.map((option) => ({ value: option.id, label: option.label })),
  ];
  return (
    <Select
      value={props.value.length === 0 ? ALL : props.value}
      onValueChange={(next) => props.onChange(next === ALL ? '' : next)}
      options={options}
      ariaLabel={props.ariaLabel}
      className="w-48"
      testId={props.testId}
    />
  );
}

function SummaryGrid(props: {
  readonly dashboard: AdminUsageDashboardResponse;
  readonly cnyPerUsd: number | null;
  readonly t: TranslationFn;
}): JSX.Element {
  const { dashboard, cnyPerUsd, t } = props;
  const summary = dashboard.summary;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
      <KpiCard
        testId="usage-summary-input-tokens"
        label={t('usage.summary.tokens_in')}
        value={formatCompactCount(summary.totalInputTokens)}
        delta={{
          text: t('usage.requests_count', { count: formatTokens(summary.totalRequests) }),
          tone: 'neutral',
        }}
      />
      <KpiCard
        testId="usage-summary-output-tokens"
        label={t('usage.summary.tokens_out')}
        value={formatCompactCount(summary.totalOutputTokens)}
        delta={{
          text: t('usage.succeeded_count', { count: formatTokens(summary.successRequests) }),
          tone: 'neutral',
        }}
      />
      <KpiCard
        testId="usage-summary-cache-write-tokens"
        label={t('usage.summary.cache_write')}
        value={formatCompactCount(summary.totalCacheWriteTokens)}
        delta={{ text: t('usage.admin.summary.cache_write_detail'), tone: 'neutral' }}
      />
      <KpiCard
        testId="usage-summary-cache-read-tokens"
        label={t('usage.summary.cache_read')}
        value={formatCompactCount(summary.totalCacheReadTokens)}
        delta={{ text: t('usage.admin.summary.cache_read_detail'), tone: 'neutral' }}
      />
      <KpiCard
        testId="usage-summary-cost"
        label={t('usage.summary.blended_spend')}
        value={formatUsdMicrosOrCentsAsCnyDisplay(
          summary.totalCostUsdMicros,
          summary.totalCostUsdCents,
          cnyPerUsd,
        )}
        delta={{ text: t('usage.admin.summary.cost_detail'), tone: 'neutral' }}
      />
      <KpiCard
        testId="usage-summary-seats-with-usage"
        // #1667: window-scoped usage and roster population are different
        // calibers, so they read as separate labelled facts rather than as one
        // "N of M" ratio that invited a false part-of-whole reading.
        label={t('usage.admin.summary.seats_with_usage')}
        value={formatTokens(summary.seatsWithUsage)}
        delta={{
          text: t('usage.admin.summary.seats_roster_detail', {
            seats: formatTokens(summary.totalSeats),
            keys: formatTokens(summary.activeApiKeys),
          }),
          tone: 'neutral',
        }}
      />
    </div>
  );
}

function TrendPanel(props: {
  readonly points: readonly AdminUsageTrendPoint[];
  readonly groupBy: string;
  readonly metric: TokenMetric;
  readonly onMetric: (metric: TokenMetric) => void;
  readonly totalTokens: number;
}): JSX.Element {
  const t = useT();
  const pivot = useMemo(() => pivotTrend(props.points, props.metric), [props.points, props.metric]);
  return (
    <Panel
      caption={t('usage.admin.trend.title')}
      padded
      testId="usage-admin-trend-card"
      actions={
        <Tabs
          value={props.metric}
          onValueChange={(next) => {
            if (next === 'total' || next === 'in' || next === 'out') props.onMetric(next);
          }}
        >
          <TabsList ariaLabel={t('usage.admin.trend.metric_aria')}>
            <TabsTrigger value="total" testId="usage-trend-metric-total">
              {t('usage.metric.total')}
            </TabsTrigger>
            <TabsTrigger value="in" testId="usage-trend-metric-in">
              {t('usage.metric.in')}
            </TabsTrigger>
            <TabsTrigger value="out" testId="usage-trend-metric-out">
              {t('usage.metric.out')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 flex items-center justify-between gap-3 text-micro text-paper-low">
          <span>{t('usage.admin.trend.subtitle', { groupBy: groupByLabel(props.groupBy, t) })}</span>
          <span className="font-mono text-paper-mid">
            {t('usage.admin.trend.total', { tokens: formatCompactCount(props.totalTokens) })}
          </span>
        </p>
        {pivot.buckets.length === 0 ? (
          <p className="m-0 py-10 text-center text-dense text-paper-low" data-testid="usage-admin-trend-empty">
            {t('usage.admin.empty.window')}
          </p>
        ) : (
          <StackedBars
            buckets={pivot.buckets}
            series={pivot.series}
            ariaLabel={t('usage.admin.trend.title')}
            testId="usage-admin-trend-chart"
          />
        )}
      </div>
    </Panel>
  );
}

function ModelMixPanel({ models }: { readonly models: readonly AdminUsageModelBreakdown[] }): JSX.Element {
  const t = useT();
  const top = models.slice(0, 6);
  return (
    <Panel caption={t('usage.model_mix.title')} padded testId="usage-model-mix-card">
      {top.length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="usage-model-mix-empty">
          {t('usage.admin.model_mix.empty')}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex h-2 overflow-hidden rounded-sm bg-well" aria-hidden="true">
            {top.map((model, index) => (
              <div
                key={`${model.vendor}:${model.model}`}
                className={stackedBarsSwatchClass(index)}
                style={{ width: `${Math.max(2, model.share * 100)}%` }}
              />
            ))}
          </div>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {top.map((model, index) => (
              <li
                key={`${model.vendor}:${model.model}`}
                className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 border-b border-line-soft pb-3 last:border-b-0 last:pb-0"
                data-testid={`usage-model-row-${testIdSlug(model.model)}`}
              >
                <span
                  className={cx('mt-1.5 h-2 w-2 rounded-[1.5px]', stackedBarsSwatchClass(index))}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="block truncate font-mono text-dense text-paper">{model.model}</span>
                  <span className="block font-mono text-micro text-paper-low">
                    {t('usage.admin.model_mix.io', {
                      input: formatCompactCount(model.inputTokens),
                      output: formatCompactCount(model.outputTokens),
                    })}
                  </span>
                </span>
                <span className="text-right">
                  <span className="block font-mono text-dense font-medium text-paper">
                    {formatCompactCount(model.totalTokens)}
                  </span>
                  <span className="block font-mono text-micro text-paper-low">
                    {formatPercent(model.share)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

function DepartmentMixPanel({
  departments,
}: {
  readonly departments: readonly AdminUsageDepartmentBreakdown[];
}): JSX.Element {
  const t = useT();
  const top = departments.slice(0, 8);
  const max = Math.max(1, ...top.map((department) => department.totalTokens));
  return (
    <Panel caption={t('usage.admin.department_mix.title')} padded testId="usage-department-mix-card">
      {top.length === 0 ? (
        <p className="m-0 text-dense text-paper-low">{t('usage.admin.department_mix.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {top.map((department) => (
            <Meter
              key={department.department ?? 'unassigned'}
              label={department.department ?? t('usage.admin.department_mix.unassigned')}
              value={department.totalTokens}
              max={max}
              valueText={formatCompactCount(department.totalTokens)}
              testId={`usage-department-row-${testIdSlug(department.department ?? 'unassigned')}`}
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

/**
 * Rate thresholds (comment-pinned so reviewers can challenge them in
 * one place): cache-hit reads `ok` above 20% (prompt caching is doing
 * real work), neutral below; rate-limited reads `warn` as soon as it
 * is non-zero and `bad` above 5% (sustained throttling).
 */
function ServiceHealthPanel({ dashboard }: { readonly dashboard: AdminUsageDashboardResponse }): JSX.Element {
  const t = useT();
  const health = dashboard.routingHealth;
  const cacheRate = health.cacheHitRate ?? 0;
  const limitedRate = health.rateLimitedRate ?? 0;
  const calm = limitedRate < 0.01;
  return (
    <Panel
      caption={t('usage.admin.service.title')}
      padded
      testId="usage-service-health-card"
      actions={
        calm ? (
          <Chip tone="ok" testId="usage-service-health-chip">
            {t('usage.admin.service.all_green')}
          </Chip>
        ) : (
          <Chip tone="warn" testId="usage-service-health-chip">
            {t('usage.admin.service.rate_limited')}
          </Chip>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <Meter
          label={t('usage.admin.service.cache_hit_rate')}
          value={cacheRate}
          max={1}
          tone={cacheRate > 0.2 ? 'ok' : 'neutral'}
          valueText={formatNullablePercent(health.cacheHitRate)}
          testId="usage-health-cache-hit"
        />
        <Meter
          label={t('usage.admin.service.rate_limited')}
          value={limitedRate}
          max={1}
          tone={limitedRate > 0.05 ? 'bad' : limitedRate > 0 ? 'warn' : 'neutral'}
          valueText={formatNullablePercent(health.rateLimitedRate)}
          testId="usage-health-rate-limited"
        />
        <dl className="m-0 grid grid-cols-1 gap-2 border-t border-line-soft pt-3">
          <HealthRow
            label={t('usage.admin.service.requests_evaluated')}
            value={formatTokens(health.routesEvaluated)}
          />
          <HealthRow
            label={t('usage.admin.service.avg_latency')}
            value={formatLatencyMs(health.averageLatencyMs)}
          />
          <HealthRow
            label={t('usage.admin.service.recovery_events')}
            value={health.failoverEvents === null ? '--' : formatTokens(health.failoverEvents)}
          />
        </dl>
      </div>
    </Panel>
  );
}

function HealthRow(props: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
      <dt className="text-label text-paper-mid">{props.label}</dt>
      <dd className="m-0 font-mono text-label text-paper">{props.value}</dd>
    </div>
  );
}

function SeatUsageTable(props: {
  readonly dashboard: AdminUsageDashboardResponse;
  readonly cnyPerUsd: number | null;
  readonly search: string;
  readonly onSearch: (value: string) => void;
  readonly onOffset: (offset: number) => void;
}): JSX.Element {
  const t = useT();
  const seats = props.dashboard.seats;
  const page = Math.floor(seats.offset / Math.max(1, seats.limit)) + 1;
  const pageCount = Math.max(1, Math.ceil(seats.total / Math.max(1, seats.limit)));
  return (
    <Panel
      caption={t('usage.admin.seat.title')}
      count={seats.total}
      testId="usage-by-seat-card"
      actions={
        <Input
          value={props.search}
          onChange={(event) => props.onSearch(event.currentTarget.value)}
          placeholder={t('usage.admin.seat.search_placeholder')}
          aria-label={t('usage.admin.seat.search_label')}
          className="w-64"
          data-testid="usage-seat-search"
        />
      }
    >
      {seats.rows.length === 0 ? (
        <p className="m-0 p-4 text-dense text-paper-low" data-testid="usage-by-seat-empty">
          {t('usage.admin.seat.empty')}
        </p>
      ) : (
        <Table className="rounded-none border-0" testId="usage-by-seat-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('usage.admin.seat.column.member')}</Th>
              <Th>{t('usage.admin.seat.column.team')}</Th>
              <Th>{t('usage.admin.seat.column.primary_model')}</Th>
              <Th num>{t('usage.summary.tokens_in')}</Th>
              <Th num>{t('usage.summary.tokens_out')}</Th>
              <Th num>{t('usage.summary.cache_write')}</Th>
              <Th num>{t('usage.summary.cache_read')}</Th>
              <Th num>{t('usage.summary.requests')}</Th>
              <Th num>{t('usage.admin.seat.column.avg_per_request')}</Th>
              <Th num>{t('usage.summary.cost')}</Th>
              <Th num>{t('usage.admin.seat.column.monthly_cap')}</Th>
              <Th>{t('usage.admin.seat.column.trend_14d')}</Th>
            </Tr>
          </THead>
          <TBody>
            {seats.rows.map((seat) => (
              <Tr key={seat.memberAccountId} data-testid={`usage-seat-row-${seat.memberAccountId}`}>
                <Td heading>
                  <span className="flex items-center gap-3">
                    <SeatAvatar label={displaySeatName(seat)} />
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-paper">
                        {displaySeatName(seat)}
                      </span>
                      <span className="block truncate font-mono text-micro text-paper-low">
                        {seat.email ?? seat.memberAccountId}
                      </span>
                    </span>
                  </span>
                </Td>
                <Td label={t('usage.admin.seat.column.team')}>
                  <Chip tone="neutral" lamp={false}>
                    {seat.department ?? t('usage.admin.department_mix.unassigned')}
                  </Chip>
                </Td>
                <Td mono label={t('usage.admin.seat.column.primary_model')}>
                  {seat.primaryModel ?? seat.defaultModel ?? '--'}
                </Td>
                <Td num label={t('usage.summary.tokens_in')}>{formatCompactCount(seat.inputTokens)}</Td>
                <Td num label={t('usage.summary.tokens_out')}>{formatCompactCount(seat.outputTokens)}</Td>
                <Td num label={t('usage.summary.cache_write')}>{formatCompactCount(seat.cacheWriteTokens)}</Td>
                <Td num label={t('usage.summary.cache_read')}>{formatCompactCount(seat.cacheReadTokens)}</Td>
                <Td num label={t('usage.summary.requests')}>{formatTokens(seat.requests)}</Td>
                <Td num label={t('usage.admin.seat.column.avg_per_request')}>
                  {seat.averageTokensPerRequest === null
                    ? '--'
                    : formatCompactCount(seat.averageTokensPerRequest)}
                </Td>
                <Td num emphasis label={t('usage.summary.cost')}>
                  {formatUsdMicrosOrCentsAsCnyMetered(
                    seat.costUsdMicros,
                    seat.costUsdCents,
                    props.cnyPerUsd,
                  )}
                </Td>
                <Td num label={t('usage.admin.seat.column.monthly_cap')}>
                  {seat.monthlyLimitUsdCents === null
                    ? '--'
                    : formatUsdCentsAsCny(seat.monthlyLimitUsdCents, props.cnyPerUsd)}
                </Td>
                <Td label={t('usage.admin.seat.column.trend_14d')} stack>
                  <Sparkline
                    values={seat.trend14d}
                    ariaLabel={t('usage.admin.seat.column.trend_14d')}
                    testId={`usage-seat-sparkline-${seat.memberAccountId}`}
                  />
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <div className="flex items-center justify-end border-t border-line-soft px-4 py-3">
        <Pagination
          page={page}
          pageCount={pageCount}
          onPageChange={(next) => props.onOffset((next - 1) * seats.limit)}
          prevLabel={t('usage.pagination.previous')}
          nextLabel={t('usage.pagination.next')}
          ariaLabel={t('usage.admin.seat.pagination_aria')}
          testId="usage-seat-pagination"
        />
      </div>
    </Panel>
  );
}

function SeatAvatar({ label }: { readonly label: string }): JSX.Element {
  const initials =
    label
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'M';
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-signal-dim font-mono text-micro text-signal"
    >
      {initials}
    </span>
  );
}

function AdminSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4" data-testid="usage-admin-loading">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        {[0, 1, 2, 3, 4, 5].map((item) => (
          <Skeleton key={item} className="h-28" />
        ))}
      </div>
      <Skeleton className="h-80" />
      <TableSkeleton rows={6} />
    </div>
  );
}

function groupByLabel(value: string, t: TranslationFn): string {
  switch (value) {
    case 'department':
      return t('usage.admin.group_by_value.department');
    case 'member':
      return t('usage.admin.group_by_value.member');
    default:
      return t('usage.admin.group_by_value.model');
  }
}

function trendValue(point: AdminUsageTrendPoint, metric: TokenMetric): number {
  if (metric === 'in') return point.inputTokens;
  if (metric === 'out') return point.outputTokens;
  return point.inputTokens + point.outputTokens;
}

/**
 * Pivot the flat trend rows into the `StackedBars` shape: sorted
 * buckets on the x axis, the top-6 groups (by metric total) as
 * series — the chart component deliberately does not know contracts
 * types.
 */
function pivotTrend(
  points: readonly AdminUsageTrendPoint[],
  metric: TokenMetric,
): { readonly buckets: readonly string[]; readonly series: readonly StackedBarsSeries[] } {
  const buckets = [...new Set(points.map((point) => point.bucket))].sort((a, b) =>
    a.localeCompare(b),
  );
  const totals = new Map<string, { label: string; total: number }>();
  for (const point of points) {
    const current = totals.get(point.groupKey) ?? { label: point.groupLabel, total: 0 };
    current.total += trendValue(point, metric);
    totals.set(point.groupKey, current);
  }
  const topGroups = [...totals.entries()]
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 6);
  const series = topGroups.map(([groupKey, meta]) => ({
    id: groupKey,
    label: meta.label,
    values: buckets.map((bucket) =>
      points
        .filter((point) => point.bucket === bucket && point.groupKey === groupKey)
        .reduce((sum, point) => sum + trendValue(point, metric), 0),
    ),
  }));
  return { buckets, series };
}

function displaySeatName(
  seat: Pick<AdminUsageSeatRow, 'memberName' | 'email' | 'memberAccountId'>,
): string {
  return seat.memberName ?? seat.email ?? seat.memberAccountId;
}

function triggerCsvDownload(csv: string, filename: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (typeof URL.createObjectURL !== 'function' || typeof URL.revokeObjectURL !== 'function') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
